import IconCheck from 'assets/icons/check.svg';
import IconExpandMore from 'assets/icons/expand-more.svg';
import cn from 'classnames';
import ChipSelector from 'components/chip-selector';
import { Locale, LOCALE_META } from 'config/constants/locales';
import { useConfig } from 'config/use-config';
import useMobile from 'hooks/common/use-mobile';
import useOnClickOutside from 'hooks/common/use-on-click-outside';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { FC, ReactNode, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Gtm } from 'tracking';
import { getNextRouterLocale } from 'utils/i18n/next-locales';
import { stripLocalePathPrefix } from 'utils/i18n/strip-locale-path-prefix';

const Sheet = dynamic(() => import('components/sheet'), { ssr: false });

export interface LocaleOption {
  locale: Locale;
  label: string;
  isVisible: boolean;
}

export const localeOptions: LocaleOption[] = (Object.keys(LOCALE_META) as Locale[]).map(
  (locale) => ({
    locale,
    label: LOCALE_META[locale].label,
    isVisible: LOCALE_META[locale].isVisible,
  })
);

/**
 * Language selector component for switching between DE and EN
 * Desktop: Shows dropdown menu
 * Mobile: Shows sheet component
 */
const LanguageSelector: FC = () => {
  const { formatMessage, locale: currentLocale } = useIntl();
  const { locale: defaultLocale } = useConfig();
  const router = useRouter();
  const { asPath } = router;
  const switchHref = stripLocalePathPrefix(asPath || '/');
  const isMobile = useMobile();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languageOptions = localeOptions.filter((option) => option.isVisible);
  const currentLanguage = localeOptions.find((lang) => lang.locale === currentLocale);

  useOnClickOutside(dropdownRef, (): void => {
    if (!isMobile) setIsOpen(false);
  });

  const handleLanguageChange = (nextLocale: Locale): void => {
    if (nextLocale === currentLocale) {
      setIsOpen(false);
      return;
    }

    Gtm.track.selectElement({
      element: ['LanguageSelector', nextLocale],
    });

    router.push(switchHref, switchHref, {
      locale: getNextRouterLocale(nextLocale, defaultLocale),
    });

    setIsOpen(false);
  };

  const renderSelectorBody = (): ReactNode => {
    if (!isMobile) {
      if (!isOpen) return null;

      return (
        <div
          className="bg-other-white border-grey-200 z-modal absolute top-full right-0 mt-2 flex min-w-40 flex-col overflow-hidden rounded-lg border shadow-lg"
          data-test="language-selector-dropdown"
        >
          {languageOptions.map((language) => (
            <button
              key={language.locale}
              className={cn(
                'text-text-light-bg-primary hover:bg-grey-100 flex items-center justify-between gap-3 px-4 py-3 text-left text-sm leading-tight transition-colors',
                language.locale === currentLocale && 'bg-grey-50 font-medium'
              )}
              onClick={() => handleLanguageChange(language.locale)}
              data-test={`language-option-${language.locale}`}
            >
              <span>{language.label}</span>
              {language.locale === currentLocale && (
                <IconCheck
                  className="text-primary-main h-3 w-3"
                  data-test="language-selected-check"
                />
              )}
            </button>
          ))}
        </div>
      );
    }

    return (
      <Sheet isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <div className="px-4 pb-8" data-test="language-selector-sheet">
          <h2 className="text-text-light-bg-primary mb-4 text-base leading-6 font-medium">
            {formatMessage({ id: 'header:language-selector:title' })}
          </h2>
          <div className="flex flex-row gap-2">
            {languageOptions.map((language) => (
              <ChipSelector
                key={language.locale}
                content={language.label}
                isSelected={language.locale === currentLocale}
                hasCircle={true}
                onClick={() => handleLanguageChange(language.locale)}
                dataTest={`language-option-${language.locale}`}
              />
            ))}
          </div>
        </div>
      </Sheet>
    );
  };

  return (
    <div className="relative" ref={dropdownRef} data-test="language-selector">
      {/* Trigger Button */}
      <button
        className="text-text-light-bg-primary flex items-center gap-0.5 text-sm"
        onClick={(): void => setIsOpen(!isOpen)}
        data-test="language-selector-trigger"
        aria-label={formatMessage({
          id: 'header:language-selector:aria-label',
        })}
        aria-expanded={isOpen}
      >
        <span>
          {formatMessage(
            { id: 'header:language-selector:label' },
            { language: currentLanguage?.label ?? languageOptions[0]?.label }
          )}
        </span>
        <IconExpandMore
          className={cn(
            'h-6 w-6 transition-transform duration-300 ease-in-out',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown / Sheet body */}
      {renderSelectorBody()}
    </div>
  );
};

export default LanguageSelector;
